import { useEffect, useState, type ReactNode } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import {
  Activity,
  Bell,
  Bot,
  Gauge,
  Radar,
  RefreshCw,
  X,
} from 'lucide-react'
import { AppChrome } from '../components/AppChrome'
import { ExecutiveOverview } from '../components/ExecutiveOverview'
import { MetricsCharts } from '../components/MetricsCharts'
import { ProjectCardView } from '../components/ProjectCard'
import {
  fetchBuildCockpit,
  fetchCockpitNotifications,
  fetchDashboard,
  fetchRoutes,
  type BuildCockpitDetail,
  type CockpitNotification,
  type DashboardSnapshot,
  type ProjectCard,
} from '../lib/api'
import { useAuth } from '../lib/auth'

type InvestorPack = {
  activation?: { score?: number; sdlc_runs?: number; mvp_ready?: number }
  ai_cost?: { total_usd?: number; per_poc_usd?: number }
  sdlc_cycle_time_hours?: { mean?: number | null; sample_size?: number }
  human_gate_latency_hours?: { mean?: number | null }
}

export function CommandCenterPage() {
  const { user, loading, authPosture } = useAuth()
  const [params, setParams] = useSearchParams()
  const [data, setData] = useState<DashboardSnapshot | null>(null)
  const [routes, setRoutes] = useState<
    { phase_id: string; tier: string; provider: string; model: string; weight_source: string }[]
  >([])
  const [notes, setNotes] = useState<CockpitNotification[]>([])
  const [unread, setUnread] = useState(0)
  const [investor, setInvestor] = useState<InvestorPack | null>(null)
  const [detail, setDetail] = useState<BuildCockpitDetail | null>(null)
  const [detailBusy, setDetailBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Sync refreshes the executive overview as well as the dashboard below it.
  const [syncTick, setSyncTick] = useState(0)

  async function load() {
    setBusy(true)
    setError(null)
    try {
      const [dash, routeRows, notePack, metrics] = await Promise.all([
        fetchDashboard(),
        fetchRoutes(),
        fetchCockpitNotifications().catch(() => ({
          unread_alerts: 0,
          notifications: [] as CockpitNotification[],
        })),
        fetch('/api/afos360/investor-metrics', { credentials: 'include' })
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null),
      ])
      setData(dash)
      setRoutes(routeRows)
      setNotes(notePack.notifications || [])
      setUnread(notePack.unread_alerts || 0)
      setInvestor(metrics)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load AFOS 360 data')
    } finally {
      setBusy(false)
    }
  }

  async function openBuild(buildId: string) {
    setDetailBusy(true)
    setError(null)
    try {
      const pack = await fetchBuildCockpit(buildId)
      setDetail(pack)
      setParams((prev) => {
        const next = new URLSearchParams(prev)
        next.set('build', buildId)
        return next
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open build cockpit')
      setDetail(null)
    } finally {
      setDetailBusy(false)
    }
  }

  function closeDetail() {
    setDetail(null)
    setParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('build')
      return next
    })
  }

  useEffect(() => {
    if (user) void load()
  }, [user])

  useEffect(() => {
    const build = params.get('build')
    if (user && build && (!detail || detail.build_id !== build)) {
      void openBuild(build)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, params])

  if (!loading && !user) return <Navigate to="/signin" replace />

  return (
    <AppChrome
      trailing={
        <button
          type="button"
          onClick={() => {
            setSyncTick((t) => t + 1)
            void load()
          }}
          className="inline-flex items-center gap-2 rounded-full border border-[var(--color-line)] px-3 py-2 text-sm text-[var(--color-mist)] hover:text-[var(--color-foam)]"
        >
          <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Sync</span>
        </button>
      }
    >
      <main className="afos-page space-y-8">
        <header>
          <h1 className="afos-page-title">Command center</h1>
          <p className="afos-page-lead">
            Tenant cockpit for the product + engineering factory — glance notifications,
            status cards, and project drill-down across SDLC, teams, and delivery.
          </p>
        </header>

        {authPosture?.banner && (
          <div
            className={`rounded-2xl border px-4 py-3 text-sm ${
              authPosture.tone === 'warning'
                ? 'border-[rgba(255,179,71,0.4)] bg-[rgba(255,179,71,0.1)] text-[var(--color-amber)]'
                : authPosture.tone === 'success'
                  ? 'border-[rgba(46,230,197,0.35)] bg-[rgba(46,230,197,0.08)] text-[var(--color-cyan)]'
                  : 'border-[var(--color-line)] bg-black/20 text-[var(--color-mist)]'
            }`}
          >
            <span className="font-semibold uppercase tracking-wider text-[10px]">
              {authPosture.mode}
            </span>
            <p className="mt-1">{authPosture.banner}</p>
          </div>
        )}

        <ExecutiveOverview refreshKey={syncTick} />

        {investor && (
          <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatTile
              icon={<Gauge className="h-5 w-5 text-[var(--color-cyan)]" />}
              label="Activation"
              value={String(investor.activation?.score ?? '—')}
            />
            <StatTile
              icon={<Radar className="h-5 w-5 text-[var(--color-amber)]" />}
              label="SDLC runs"
              value={String(investor.activation?.sdlc_runs ?? '—')}
            />
            <StatTile
              icon={<Activity className="h-5 w-5 text-[var(--color-rose)]" />}
              label="AI $/POC"
              value={
                investor.ai_cost?.per_poc_usd != null
                  ? `$${Number(investor.ai_cost.per_poc_usd).toFixed(2)}`
                  : '—'
              }
            />
            <StatTile
              icon={<Bot className="h-5 w-5 text-[var(--color-cyan)]" />}
              label="Cycle hrs (mean)"
              value={
                investor.sdlc_cycle_time_hours?.mean != null
                  ? String(investor.sdlc_cycle_time_hours.mean)
                  : '—'
              }
            />
          </section>
        )}

        {/* Quick-glance notifications */}
        <section className="glass rounded-3xl p-4 sm:p-5">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-[var(--color-foam)]">
              <Bell className="h-4 w-4 text-[var(--color-amber)]" />
              Cockpit notifications
            </div>
            <span className="rounded-full border border-[var(--color-line)] px-3 py-1 text-[10px] uppercase tracking-[0.16em] text-[var(--color-mist)]">
              {unread} alert{unread === 1 ? '' : 's'} firing
            </span>
          </div>
          {notes.length === 0 ? (
            <p className="text-sm text-[var(--color-mist)]">
              All quiet — no firing SLO alerts or MVP events yet.
            </p>
          ) : (
            <ul className="grid gap-2 md:grid-cols-2">
              {notes.slice(0, 6).map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => n.build_id && void openBuild(n.build_id)}
                    className={`w-full rounded-2xl border px-3 py-3 text-left text-sm ${
                      n.kind === 'alert'
                        ? 'border-[rgba(255,107,138,0.35)] bg-[rgba(255,107,138,0.08)]'
                        : 'border-[var(--color-line)] bg-black/20'
                    }`}
                  >
                    <div className="font-semibold text-[var(--color-foam)]">{n.title}</div>
                    <div className="mt-1 text-[var(--color-mist)]">{n.body}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
          <StatTile
            icon={<Radar className="h-5 w-5 text-[var(--color-cyan)]" />}
            label="Active builds"
            value={String(data?.project_cards.length ?? '—')}
          />
          <StatTile
            icon={<Gauge className="h-5 w-5 text-[var(--color-amber)]" />}
            label="MVP ready"
            value={String(data?.project_cards.filter((c) => c.mvp_ready).length ?? '—')}
          />
          <StatTile
            icon={<Bot className="h-5 w-5 text-[var(--color-rose)]" />}
            label="Metric families"
            value={String(data?.metric_families.length ?? '—')}
          />
        </section>

        {data?.factory_ops?.available && (
          <section>
            <div className="mb-4 flex items-end justify-between gap-3">
              <div>
                <h2 className="font-[family-name:var(--font-display)] text-2xl font-extrabold sm:text-3xl">
                  Factory ops
                </h2>
                <p className="text-sm text-[var(--color-mist)]">
                  SDLC funnel, ephemeral health, AI burn — product-native monitoring
                </p>
              </div>
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-[var(--color-mist)]">
                <Activity className="h-3.5 w-3.5 text-[var(--color-cyan)]" />
                Live
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {(data.factory_ops.cards || []).map((card) => (
                <div
                  key={card.id}
                  className="rounded-3xl border border-[var(--color-line)] bg-black/25 px-4 py-5"
                >
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-mist)]">
                    {card.label}
                  </div>
                  <div className="mt-2 font-[family-name:var(--font-display)] text-3xl font-extrabold text-[var(--color-foam)]">
                    {card.value}
                  </div>
                  <p className="mt-2 text-sm text-[var(--color-mist)]">{card.hint}</p>
                </div>
              ))}
            </div>
            {(data.factory_ops.alerts || []).length > 0 && (
              <div className="mt-5 space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-[var(--color-foam)]">
                  <Bell className="h-4 w-4 text-[var(--color-amber)]" />
                  SLO alerts
                </div>
                {(data.factory_ops.alerts || []).map((alert) => (
                  <div
                    key={alert.id}
                    className={`rounded-2xl border px-4 py-3 text-sm ${
                      alert.status === 'firing'
                        ? 'border-[rgba(255,107,138,0.35)] bg-[rgba(255,107,138,0.08)] text-[var(--color-rose)]'
                        : 'border-[var(--color-line)] bg-black/20 text-[var(--color-mist)]'
                    }`}
                  >
                    <span className="font-semibold text-[var(--color-foam)]">{alert.name}</span>
                    <span className="mx-2 text-[var(--color-mist)]">·</span>
                    {alert.summary}
                    <span className="ml-2 uppercase tracking-wider text-[10px] opacity-80">
                      {alert.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {error && (
          <div className="rounded-2xl border border-[rgba(255,107,138,0.4)] bg-[rgba(255,107,138,0.1)] px-4 py-3 text-sm text-[var(--color-rose)]">
            {error}
          </div>
        )}

        <section>
          <div className="mb-4">
            <h2 className="font-[family-name:var(--font-display)] text-2xl font-extrabold sm:text-3xl">
              Project orbit
            </h2>
            <p className="text-sm text-[var(--color-mist)]">
              Click a card to drill into phases, skill grades, success score, and related SDLC
            </p>
          </div>
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,24rem)]">
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-2">
              <AnimatePresence>
                {(data?.project_cards || []).map((card, index) => (
                  <ProjectCardView
                    key={card.id}
                    card={card}
                    index={index}
                    selected={detail?.build_id === card.id}
                    onOpen={(c: ProjectCard) => void openBuild(c.id)}
                  />
                ))}
              </AnimatePresence>
              {!data?.project_cards?.length && !busy && (
                <div className="glass col-span-full rounded-3xl p-6 text-center text-sm text-[var(--color-mist)] sm:p-8">
                  No builds yet. Run{' '}
                  <code className="break-all text-[var(--color-cyan)]">
                    python -m afos360 init &quot;&lt;idea&gt;&quot;
                  </code>{' '}
                  then{' '}
                  <code className="break-all text-[var(--color-cyan)]">
                    python -m afos360 run &lt;id&gt;
                  </code>
                  .
                </div>
              )}
            </div>

            <aside className="glass sticky top-24 h-fit rounded-3xl p-5">
              {!detail && !detailBusy && (
                <p className="text-sm text-[var(--color-mist)]">
                  Select a project card for Grafana-style drill-down: success grade, phase
                  timeline, skill scores, artifacts, and related ticket/ephemeral runs.
                </p>
              )}
              {detailBusy && (
                <p className="text-sm text-[var(--color-mist)]">Loading build cockpit…</p>
              )}
              {detail && !detailBusy && (
                <BuildDetailPanel detail={detail} onClose={closeDetail} />
              )}
            </aside>
          </div>
        </section>

        {data && (
          <section>
            <h2 className="mb-4 font-[family-name:var(--font-display)] text-2xl font-extrabold sm:text-3xl">
              Live graphs
            </h2>
            <div className="afos-scroll-x">
              <MetricsCharts data={data} />
            </div>
          </section>
        )}

        <section className="glass rounded-3xl p-4 sm:p-5">
          <h2 className="mb-4 font-[family-name:var(--font-display)] text-xl font-bold sm:text-2xl">
            Model routes
          </h2>
          <div className="grid gap-2 md:grid-cols-2">
            {routes.map((route, i) => (
              <motion.div
                key={route.phase_id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.03 }}
                className="flex items-center justify-between gap-3 rounded-2xl border border-[var(--color-line)] bg-black/20 px-3 py-3 sm:px-4"
              >
                <div className="min-w-0">
                  <div className="font-semibold">{route.phase_id}</div>
                  <div className="truncate font-[family-name:var(--font-mono)] text-xs text-[var(--color-mist)]">
                    {route.provider}/{route.model}
                  </div>
                </div>
                <span className="shrink-0 rounded-full bg-white/5 px-3 py-1 text-xs uppercase tracking-wider text-[var(--color-cyan)]">
                  {route.tier}
                </span>
              </motion.div>
            ))}
          </div>
        </section>
      </main>
    </AppChrome>
  )
}

function BuildDetailPanel({
  detail,
  onClose,
}: {
  detail: BuildCockpitDetail
  onClose: () => void
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-[family-name:var(--font-mono)] text-[10px] uppercase tracking-[0.18em] text-[var(--color-mist)]">
            {detail.build_id}
          </p>
          <h3 className="mt-1 font-[family-name:var(--font-display)] text-xl font-bold text-[var(--color-foam)]">
            {detail.card.name}
          </h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full border border-[var(--color-line)] p-2 text-[var(--color-mist)] hover:text-[var(--color-foam)]"
          aria-label="Close build detail"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <MiniStat label="Success" value={`${detail.analytics.success_pct}%`} />
        <MiniStat label="Grade" value={detail.analytics.grade} />
        <MiniStat label="Phase" value={detail.card.phase} />
        <MiniStat
          label="Skill avg"
          value={
            detail.mean_skill_grade != null ? detail.mean_skill_grade.toFixed(2) : '—'
          }
        />
      </div>

      <div>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-mist)]">
          Phases
        </h4>
        <ul className="max-h-40 space-y-1 overflow-y-auto text-sm">
          {(detail.phases.length ? detail.phases : [{ id: detail.card.phase, status: detail.mvp_status.status }]).map(
            (p, i) => (
              <li
                key={`${p.id}-${i}`}
                className="flex items-center justify-between rounded-xl border border-[var(--color-line)] bg-black/20 px-3 py-2"
              >
                <span className="text-[var(--color-foam)]">{p.id || 'phase'}</span>
                <span className="text-[var(--color-mist)]">{p.status || '—'}</span>
              </li>
            ),
          )}
        </ul>
      </div>

      {detail.skill_grades.length > 0 && (
        <div>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-mist)]">
            Skill grades
          </h4>
          <ul className="max-h-36 space-y-1 overflow-y-auto text-sm">
            {detail.skill_grades.map((g) => (
              <li
                key={g.phase_id}
                className="flex items-center justify-between rounded-xl border border-[var(--color-line)] px-3 py-2"
              >
                <span>{g.phase_id}</span>
                <span className="font-[family-name:var(--font-mono)] text-[var(--color-cyan)]">
                  {g.mean != null ? Number(g.mean).toFixed(2) : '—'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(detail.analytics.recommendations || []).length > 0 && (
        <div>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-mist)]">
            Next actions
          </h4>
          <ul className="space-y-1 text-sm text-[var(--color-mist)]">
            {(detail.analytics.recommendations || []).slice(0, 4).map((tip) => (
              <li key={tip}>· {tip}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        <Link
          to="/workforce"
          className="rounded-full border border-[var(--color-line)] px-3 py-1.5 text-xs text-[var(--color-foam)]"
        >
          Workforce
        </Link>
        <Link
          to="/ephemeral"
          className="rounded-full border border-[var(--color-line)] px-3 py-1.5 text-xs text-[var(--color-foam)]"
        >
          Ephemeral
        </Link>
        <Link
          to="/tickets"
          className="rounded-full border border-[var(--color-line)] px-3 py-1.5 text-xs text-[var(--color-foam)]"
        >
          Tickets
        </Link>
      </div>
    </div>
  )
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-[var(--color-line)] bg-black/25 px-3 py-2">
      <div className="text-[10px] uppercase tracking-[0.16em] text-[var(--color-mist)]">
        {label}
      </div>
      <div className="mt-1 font-[family-name:var(--font-display)] text-lg font-bold text-[var(--color-foam)]">
        {value}
      </div>
    </div>
  )
}

function StatTile({
  icon,
  label,
  value,
}: {
  icon: ReactNode
  label: string
  value: string
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass rounded-3xl p-5"
    >
      <div className="mb-3 flex items-center gap-2 text-sm uppercase tracking-[0.18em] text-[var(--color-mist)]">
        {icon}
        {label}
      </div>
      <div className="font-[family-name:var(--font-display)] text-4xl font-extrabold text-[var(--color-foam)]">
        {value}
      </div>
    </motion.div>
  )
}
